import { jsonBody } from './http';

/**
 * The browser's half of a module's API, written once.
 *
 * Every module had its own byte-identical `request` — same `fetch`, same error
 * class, same 204 handling — and the copies were not the problem. What they
 * cost was everything the core's client learned afterwards: `D74`'s payload
 * hash went in once and the SES copy went without, and then `016`'s step-up
 * retry went in once and *all three* copies went without. The second was
 * invisible until a deployment with `requireMfaForWrites` on, where every
 * module write was refused and reported as "That did not work. Try again."
 *
 * `apps/ui/src/api.ts` said, in a comment, that "every write in the core and
 * in every module comes through `request`". This is the file that makes that
 * true. The lesson is the same one twice, so the rule follows the code: ESLint
 * no longer exempts a module's client from the `fetch` ban.
 */

/**
 * One error class for every module.
 *
 * Each client re-exports it under its own name, so `instanceof DnsApiError`
 * still reads as it did — but a refusal is now the same shape everywhere,
 * which is what lets the retry below live in one place.
 */
export class ModuleApiError extends Error {
	constructor(
		readonly status: number,
		/** The API's own `error` field where it sent one, else a generic code. */
		readonly code: string,
		readonly detail?: string,
		/** So a write's failure can read differently from a read's. */
		readonly method: string = 'GET',
	) {
		super(detail ?? code);
		this.name = 'ModuleApiError';
	}
}

/**
 * The two refusals any write can meet (`016`), and the only two this retries.
 *
 * `step-up` is answerable: the caller enters a code and the request is sent
 * again. `mfa-required` is a dead end by design — no code clears it — so the
 * handler explains it and the original error is rethrown.
 */
export type RefusalReason = 'step-up' | 'mfa-required';

export type StepUpHandler = (reason: RefusalReason) => Promise<boolean>;

/**
 * **One handler, for the core and every module.**
 *
 * Registered once by the shell's step-up dialog. It lives here rather than in
 * `apps/ui` because a module cannot import from the app — which is exactly why
 * the modules had no step-up handling at all: the seam existed somewhere they
 * could not reach.
 *
 * Resolves `true` when the caller stepped up and the request should be tried
 * again, `false` when they dismissed it.
 */
let stepUpHandler: StepUpHandler | undefined;

export function onStepUpRequired(handler: StepUpHandler): void {
	stepUpHandler = handler;
}

/** For tests, which must not inherit a handler from the suite before. */
export function resetStepUpHandler(): void {
	stepUpHandler = undefined;
}

/** `undefined` for anything the dialog cannot help with. */
export function refusalReasonOf(status: number, code: string): RefusalReason | undefined {
	if (status === 401 && code === 'step-up') return 'step-up';
	if (status === 403 && code === 'mfa-required') return 'mfa-required';
	return undefined;
}

/**
 * Runs a request, and runs it once more if the caller steps up.
 *
 * Exported rather than kept private because the core's client has its own
 * error type and its own `send`, and the thing that has to be shared is the
 * **handler and the retry**, not the plumbing around them. Two clients reading
 * one `stepUpHandler` is the whole point.
 *
 * Once, not in a loop. A second refusal after a successful step-up is not
 * something another code will fix, and retrying would ask for codes until the
 * user gave up.
 */
export async function withStepUp<T>(
	attempt: () => Promise<T>,
	reasonOf: (error: unknown) => RefusalReason | undefined,
): Promise<T> {
	try {
		return await attempt();
	} catch (error) {
		if (stepUpHandler === undefined) throw error;

		const reason = reasonOf(error);
		if (reason === undefined) throw error;

		/**
		 * The handler is told either way, because explaining a dead end is
		 * the only thing that can be done about one.
		 */
		const steppedUp = await stepUpHandler(reason);

		/**
		 * **Only `step-up` is retryable**, whatever the handler answers.
		 *
		 * The core's version left this to the dialog: it retried on any
		 * `true`, and relied on the dialog never returning one for
		 * `mfa-required`. That held, but it is an unwritten contract between
		 * two files — and the comment beside it already claimed the retry
		 * layer rethrows. It does now.
		 */
		if (reason !== 'step-up' || !steppedUp) throw error;
		return attempt();
	}
}

/**
 * Sends one file to a URL somebody else signed.
 *
 * **Not through `createModuleClient`, and deliberately so.** That client adds
 * `x-amz-content-sha256` for CloudFront's origin access control (`D74`); a
 * presigned S3 URL carries its own signature over an exact set of headers, and
 * an extra one makes the request fail with `SignatureDoesNotMatch`. The two
 * are opposite requirements and must not share a code path.
 *
 * Here rather than in a module because the `fetch` ban (`D117`) is absolute in
 * module UI code and should stay that way — "upload a file to a URL somebody
 * signed" is a generic capability, not one module's business.
 *
 * No progress reporting: `fetch` cannot report upload progress, and a large
 * file therefore shows only a busy state. Worth replacing with `XMLHttpRequest`
 * when somebody uploads something big enough to mind.
 */
export async function putSignedFile(url: string, file: Blob, contentType: string): Promise<void> {
	const response = await fetch(url, {
		method: 'PUT',
		body: file,
		headers: { 'Content-Type': contentType },
		/**
		 * The signature **is** the authorisation. Sending the dashboard's
		 * cookies to S3 would achieve nothing and hand a third party a
		 * session.
		 */
		credentials: 'omit',
	});

	if (!response.ok) {
		throw new ModuleApiError(response.status, 'upload_failed', await response.text());
	}
}

export interface RequestOptions {
	readonly method?: string;
	readonly body?: unknown;
	/**
	 * Parameters for the URL, encoded here rather than by each caller.
	 *
	 * This is how a `DELETE` says *which* thing to remove — see the refusal
	 * below for why it cannot be said in a body (`D138`).
	 */
	readonly query?: Record<string, string>;
}

export type ModuleRequest = <T>(path: string, init?: RequestOptions) => Promise<T>;

/**
 * A module's `request`, bound to where the core mounted it.
 *
 * The base path is the only thing a module client now says for itself. A
 * module never knows where it was mounted beyond that string.
 */
export function createModuleClient(base: string): ModuleRequest {
	async function send<T>(path: string, init: RequestOptions): Promise<T> {
		/**
		 * **A `DELETE` may not carry a body** (`D138`).
		 *
		 * CloudFront's origin access control signs the request to the Lambda's
		 * function URL, and the body is covered only by the
		 * `x-amz-content-sha256` this client computes. For `DELETE` the body
		 * does not survive to the origin, so the origin hashes nothing, the
		 * hashes disagree, and the function URL rejects the signature — with a
		 * message about a secret access key, which is the opposite of a clue.
		 *
		 * It fails only in a deployment: there is no origin access control in
		 * front of the dev server, so this is exactly the class of bug `D90`
		 * is about. Hence a refusal here rather than a note somewhere.
		 */
		if ((init.method ?? 'GET').toUpperCase() === 'DELETE' && init.body !== undefined) {
			throw new Error(
				`DELETE ${path} cannot carry a body: CloudFront does not sign one, and the request fails only once deployed. Name what to remove in \`query\` instead.`,
			);
		}

		/**
		 * The content type and the payload hash CloudFront will not compute
		 * (`D74`). CloudFront signs the method, the path and the headers it
		 * forwards — not the body — so a write without this fails on a
		 * signature mismatch in a deployment and succeeds locally, where there
		 * is no origin access control in front of the dev server.
		 */
		const sending = init.body === undefined ? undefined : await jsonBody(init.body);

		const search =
			init.query === undefined ? '' : `?${new URLSearchParams(init.query).toString()}`;

		const response = await fetch(`${base}${path}${search}`, {
			method: init.method ?? 'GET',
			credentials: 'same-origin',
			headers: { Accept: 'application/json', ...sending?.headers },
			...(sending === undefined ? {} : { body: sending.body }),
		});

		if (response.status === 204) return undefined as T;
		const payload: unknown = await response.json().catch(() => undefined);

		if (!response.ok) {
			const body = (payload ?? {}) as { error?: string; message?: string };
			throw new ModuleApiError(
				response.status,
				body.error ?? 'unknown_error',
				body.message,
				init.method ?? 'GET',
			);
		}
		return payload as T;
	}

	return <T>(path: string, init: RequestOptions = {}): Promise<T> =>
		withStepUp(
			() => send<T>(path, init),
			(error) =>
				error instanceof ModuleApiError
					? refusalReasonOf(error.status, error.code)
					: undefined,
		);
}
